import {
  FormOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { RichTextButton } from '@framework/form/inputs/rich-text/menu/RichTextMenuButton.tsx';
import {
  type IPopoverHandle,
  Popover,
} from '@framework/ui/components/popover/Popover.tsx';
import { type Editor } from '@tiptap/react';
import { Button, Layout, Space } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  type ComputeExpressionReferenceAttributes,
  ComputeExpressionReferenceNode,
} from '../../../schema/expressions/compute/input/nodes/ComputeExpressionReferenceNode.tsx';
import { PlaceholderCategoryFields } from './placeholder/PlaceholderCategoryFields.tsx';
import { PlaceholderCategoryMenu } from './placeholder/PlaceholderCategoryMenu.tsx';
import { staticPlaceholderReferences } from './placeholder/staticPlaceholderReferences.ts';
import {
  type PlaceholderCategory,
  type PlaceholderReference,
  placeholderCategories,
} from './placeholder/types.ts';

const { Header, Sider } = Layout;

const CategorySider = styled(Sider)`
  padding: 0;
  background: ${props => props.theme.colorBgContainer};

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
  }

  .ant-menu {
    flex: 1;
  }
`;

interface Props {
  editor: Editor | null;
  references: PlaceholderReference[];
  staticReferences?: PlaceholderReference[];
}

export function PlaceholderRichTextButton(props: Props) {
  const { editor, references, staticReferences } = props;
  const theme = useTheme();

  const allReferences = useMemo(() => {
    return [
      ...staticPlaceholderReferences,
      ...(staticReferences ?? []),
      ...references,
    ];
  }, [references, staticReferences]);

  const groupedReferences = useMemo(() => {
    return allReferences.reduce(
      (acc, reference) => {
        if (reference.category) {
          acc[reference.category] = [
            ...(acc[reference.category] || []),
            reference,
          ];
        }
        return acc;
      },
      {} as Record<string, PlaceholderReference[]>,
    );
  }, [allReferences]);
  const popoverRef = useRef<IPopoverHandle>(null);

  const [selectedCategory, setSelectedCategory] =
    useState<PlaceholderCategory>();
  const [selectedCode, setSelectedCode] = useState<string | undefined>();

  const handleSelectCategory = useCallback((category: PlaceholderCategory) => {
    setSelectedCategory(category);
    setSelectedCode(undefined);
  }, []);

  const categoryFields = useMemo(() => {
    if (selectedCategory) {
      return groupedReferences[selectedCategory];
    }
    const firstCategory = placeholderCategories.find(
      category => groupedReferences[category],
    );
    if (firstCategory) {
      return groupedReferences[firstCategory];
    }
    return [];
  }, [groupedReferences, selectedCategory]);

  useEffect(() => {
    if (!(selectedCategory && groupedReferences[selectedCategory])) {
      handleSelectCategory(placeholderCategories[0]);
    }
  }, [groupedReferences, handleSelectCategory, selectedCategory]);

  const handleClose = () => {
    popoverRef.current?.close();
  };

  const handleInsert = (reference: ComputeExpressionReferenceAttributes) => {
    if (!(reference && editor)) return;

    editor
      .chain()
      .focus()
      .insertContent([
        {
          type: ComputeExpressionReferenceNode.name,
          attrs: reference,
        },
        {
          type: 'text',
          text: ' ',
        },
      ])
      .run();
    handleClose();
  };

  if (!editor) return null;

  return (
    <Popover
      ref={popoverRef}
      placement="bottomLeft"
      overlayStyle={{ width: '670px' }}
      overlayInnerStyle={{
        padding: 0,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
      content={
        <Layout>
          <Header
            style={{
              padding: '12px',
              borderBottom: `1px solid ${theme.colorBorderSecondary}`,
              height: 'auto',
              lineHeight: 'unset',
            }}
          >
            <Space.Compact size="large">
              <Button type="primary" icon={<UnorderedListOutlined />}>
                Sfoglia
              </Button>
              {/* TEMP: Bottone placeholder per la ricerca */}
              <Button icon={<SearchOutlined />}>Cerca</Button>
            </Space.Compact>
          </Header>

          <Layout>
            <CategorySider width={'33%'}>
              <PlaceholderCategoryMenu
                groupedReferences={groupedReferences}
                onSelect={handleSelectCategory}
              />
            </CategorySider>

            <PlaceholderCategoryFields
              category={selectedCategory}
              fields={categoryFields}
              onClose={handleClose}
              onInsert={handleInsert}
              selectedCode={selectedCode}
              setSelectedCode={setSelectedCode}
            />
          </Layout>
        </Layout>
      }
    >
      <RichTextButton
        icon={<FormOutlined />}
        title="Riferimento a Placeholder"
        disabled={!editor.isEditable}
      >
        Placeholder
      </RichTextButton>
    </Popover>
  );
}
